import { createContext, useMemo, type ReactNode } from "react";

import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    useHomePageJPDXflowTargetingCohort,
    useHomePageJSMXflowTargetingCohort,
    useHomePageLoomXflowTargetingCohort,
    useLoggedInHasJiraAndNotConfluence,
    useLoggedOut
} from "@/components/user-context/helpers/cohorts";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";
import { GlobalFooter } from "@/patterns/global-footer/src/global-footer";
import { GlobalNav } from "@/patterns/global-nav/src/nav-global";
import { FeatureExposed } from "@/utils/feature-exposed/use-feature-exposed";
import { ParentScrollTracking } from "@/utils/scroll-tracking/parent-scroll-tracker";

import StickyContainer from "../../../../patterns/sticky-container/sticky-container";

import { HomePageBannerAlert } from "./sections-v2/banner-alert.tsx";
import { CustomerStories } from "./sections-v2/customer-stories.tsx";
import { FutureOfTeamwork } from "./sections-v2/future-of-teamwork.tsx";
import { GetStartedCTA } from "./sections-v2/get-started-cta.tsx";
import { HeroJiraCtaTemplate } from "./sections-v2/hero-jira-cta-template.tsx";
import { HeroTakeoverTeam26 } from "./sections-v2/hero-takeover-team-26";
import { HeroTWCHeader } from "./sections-v2/hero-twc-header";
import { HeroTeamworkCollection } from "./sections-v2/hero-twc-tabs";
import { ConfluenceCrossflowCTA } from "./sections-v2/homepage-confluence-crossflow-cta.tsx";
import { JiraLoggedInHeader } from "./sections-v2/jira-logged-in-header";
import { JPDCrossflowComponent } from "./sections-v2/jpd-xflow-hero/homepage-jpd-xflow";
import { ContentTypesSection } from "./sections-v2/jsm-xflow-hero/content-types-section";
import { JSMCrossflowComponent } from "./sections-v2/jsm-xflow-hero/homepage-jsm-xflow";
import { LoggedInHeroContainer } from "./sections-v2/logged-in-hero-container.tsx";
import { LoomCrossflowComponent } from "./sections-v2/loom-xflow-hero/homepage-loom-xflow";
import { ProductProvider } from "./sections-v2/reusable-xflow-hero-component/product-context";
import { RovoInnovationsVideo } from "./sections-v2/rovo-innovations-video.tsx";
import { ScrollPinnedSections } from "./sections-v2/scroll-pinned-sections.tsx";
import { TabsAndIntegrationsSections } from "./sections-v2/tabs.tsx";
import { TeamStats } from "./sections-v2/team-stats.tsx";
import { TeamworkCollectionHero } from "./sections-v2/teamwork-collection-hero.tsx";

export const HeroSelectedTeamIndexContext = createContext<number | undefined>(
    undefined
);

type CrossflowProduct = "jsm" | "jpd" | "loom" | "confluence";

function getHomePageQueryParams(): URLSearchParams | null {
    if (typeof window !== "undefined") {
        return new URLSearchParams(window.location.search);
    }
    if (globalThis.location) {
        return new URLSearchParams(globalThis.location.search);
    }
    return null;
}

/**
 * Determines which targeting cohort is enabled and returns the corresponding product.
 * Checks JSM first, then JPD, then Loom.
 */
const useGetEnabledCrossflowProduct = (): CrossflowProduct | null => {
    const isConfEnabled = useLoggedInHasJiraAndNotConfluence();
    const isJsmEnabled = useHomePageJSMXflowTargetingCohort();
    const isJpdEnabled = useHomePageJPDXflowTargetingCohort();
    const isLoomEnabled = useHomePageLoomXflowTargetingCohort();

    return useMemo(() => {
        if (isConfEnabled) return "confluence";
        if (isJsmEnabled) return "jsm";
        if (isJpdEnabled) return "jpd";
        if (isLoomEnabled) return "loom";
        return null;
    }, [isConfEnabled, isJsmEnabled, isJpdEnabled, isLoomEnabled]);
};

/**
 * Default Jira CTA hero shown when user is not enrolled in any crossflow experiments
 */
const DefaultJiraHero = () => (
    <HeroJiraCtaTemplate>
        <JiraLoggedInHeader />
    </HeroJiraCtaTemplate>
);

/**
 * Renders the appropriate crossflow component based on the enabled product.
 */
const renderHeroForCrossflowProduct = (
    product: CrossflowProduct | null
): ReactNode => {
    switch (product) {
        case "confluence":
            return (
                <FeatureExposed
                    experimentKey="gwp-5831-homepage-confluence-xflow"
                    group="Test"
                >
                    <ProductProvider product="confluence">
                        <ConfluenceCrossflowCTA
                            contentComponent={ContentTypesSection}
                        />
                    </ProductProvider>
                </FeatureExposed>
            );
        case "jsm":
            return (
                <ProductProvider product="jsm">
                    <JSMCrossflowComponent
                        contentComponent={ContentTypesSection}
                    />
                </ProductProvider>
            );
        case "jpd":
            return (
                <ProductProvider product="jpd">
                    <JPDCrossflowComponent
                        contentComponent={ContentTypesSection}
                    />
                </ProductProvider>
            );
        case "loom":
            return (
                <ProductProvider product="loom">
                    <LoomCrossflowComponent
                        contentComponent={ContentTypesSection}
                    />
                </ProductProvider>
            );
        default:
            return <DefaultJiraHero />;
    }
};

export const HomePageTemplate = () => {
    const isLoggedIn = !useLoggedOut();
    const enabledCrossflowProduct = useGetEnabledCrossflowProduct();

    const queryParams = getHomePageQueryParams();
    const isTeamchangerVariant =
        queryParams?.get("teamchangerReferral") === "true";

    const heroSection: ReactNode = useMemo(() => {
        if (isTeamchangerVariant) {
            return (
                <HeroTeamworkCollection>
                    <HeroTWCHeader />
                </HeroTeamworkCollection>
            );
        }
        if (!isLoggedIn) {
            return <DefaultJiraHero />;
        }
        return (
            <LoggedInHeroContainer>
                {renderHeroForCrossflowProduct(enabledCrossflowProduct)}
            </LoggedInHeroContainer>
        );
    }, [isTeamchangerVariant, isLoggedIn, enabledCrossflowProduct]);

    return (
        <UFOSegment name="home-page-v2">
            <div>
                <StickyContainer>
                    <HomePageBannerAlert />
                    <GlobalNav productKey={"home"} />
                </StickyContainer>
                <TypographyStyles>
                    <main id="content">
                        <ParentScrollTracking
                            scrollTrackingEnabled={true}
                            pageId="home-page-v2"
                        >
                            <HeroTakeoverTeam26 />
                            {heroSection}
                            <RovoInnovationsVideo />
                            <TeamworkCollectionHero />
                            <ScrollPinnedSections />
                            <TeamStats />
                            <TabsAndIntegrationsSections />
                            <CustomerStories />
                            <FutureOfTeamwork />
                            <GetStartedCTA />
                        </ParentScrollTracking>
                    </main>
                </TypographyStyles>
                <GlobalFooter />
            </div>
        </UFOSegment>
    );
};
